import { Injectable } from '@angular/core';
import { BaseResponseModel, BaseSearchResponseModel } from '@d-models';
import {
  EventLogModel,
  IModuleTableRes,
  ModuleTableResModel,
} from '@d-models/services/audit.model';
import { CoreUserService } from '@d-services/core/user.service';
import { forkJoin, from, Observable, switchMap } from 'rxjs';
import { HttpService } from '../other/http.service';
import dayjs from 'dayjs';
import { USER_LEVEL } from '@d-constants/user.constant';
import { AuthService } from './auth.service';
@Injectable({ providedIn: 'root' })
export class AuditService {
  constructor(
    private httpService: HttpService,
    private authService: AuthService,
  ) {}

  private baseUrl = 'audit-svc';

  searchEventLog(
    event: any,
  ): Observable<BaseSearchResponseModel<EventLogModel>> {
    const url = `${this.baseUrl}/event-log/search`;
    const payload = this.httpService.buildSearchRequest(event);
    return this.httpService.postSearch<EventLogModel>(
      url,
      payload,
      EventLogModel,
    );
  }

  createEventLog(module: string, event: string, recordId?: string) {
    const url = `${this.baseUrl}/event-log/create`;

    return forkJoin([
      this.getAppPlatform(),
      this.getLocation(),
      this.authService.getCurrentUser(),
    ])
      .pipe(
        switchMap(([platform, location, currentUser]) => {
          const payload = EventLogModel.createPayload({
            recordId,
            userId: currentUser.id,
            userName: currentUser.name,
            event,
            module,
            appPlatform: platform.appPlatform,
            browser: platform.browser,
            browserVersion: platform.browserVersion,
            mobileModel: platform.mobileModel,
            mobile_OS_Version: platform.mobile_OS_Version,
            clientIMEI: '',
            clientHostName: '',
            clientIP: '',
            clientPort: '',
            clientLatitude: location.latitude ? '' + location.latitude : '',
            clientLongitude: location.longitude ? '' + location.longitude : '',
          });

          return this.httpService.post(url, payload, {}, true);
        }),
      )
      .subscribe();
  }

  private getAppPlatform() {
    return new Observable<{
      appPlatform: string;
      browser: string;
      browserVersion: string;
      mobileModel: string;
      mobile_OS_Version: string;
    }>((sub) => {
      if ((navigator as any).userAgentData) {
        from(
          navigator['userAgentData'].getHighEntropyValues([
            'architecture',
            'model',
            'platformVersion',
            'fullVersionList',
          ]),
        ).subscribe((val: any) => {
          sub.next({
            appPlatform: this.platFormWithVersion(
              val.platform,
              val.platformVersion,
            ),
            browser: val.brands[0].brand,
            browserVersion: val.brands[0].version,
            mobileModel: val.model,
            mobile_OS_Version: val.platformVersion,
          });
          sub.complete();
        });
      } else {
        sub.next({
          appPlatform: '',
          browser: '',
          browserVersion: '',
          mobileModel: '',
          mobile_OS_Version: '',
        });
        sub.complete();
      }
    });
  }

  private platFormWithVersion(platform: string, version: string) {
    if (platform === 'Windows') {
      return (
        'Windows ' +
        `${
          +version.split('.')[0] >= 10
            ? +version.split('.')[0] >= 13
              ? 11
              : 10
            : version
        }`
      );
    }
    return platform + ' ' + version;
  }

  private getLocation() {
    return new Observable<{
      latitude: number | null;
      longitude: number | null;
    }>((sub) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (e) => {
            sub.next({
              latitude: e.coords.latitude,
              longitude: e.coords.longitude,
            });
            sub.complete();
          },
          (e) => {
            sub.next({
              latitude: null,
              longitude: null,
            });
            sub.complete();
          },
        );
      } else {
        sub.next({ latitude: null, longitude: null });
        sub.complete();
      }
    });
  }

  searchAuditTrail(event) {
    const url = `${this.baseUrl}/audit-log/search`;
    const payload = this.httpService.buildSearchRequest(event);
    const filterDate = payload.advancedFilter?.['date'];
    if (Array.isArray(filterDate)) {
      payload.advancedFilter = {
        ...payload.advancedFilter,
        fromDate: dayjs(filterDate[0]).startOf('d').unix(),
        toDate: dayjs(filterDate[1]).endOf('d').unix(),
      };
      delete payload.advancedFilter?.['date'];
    }
    return this.httpService.postSearch<EventLogModel>(
      url,
      payload,
      EventLogModel,
    );
  }

  getModuleTableOfAuditTrail() {
    const url = `${this.baseUrl}/audit-log/module-tables`;
    return this.httpService.get<IModuleTableRes>(url, ModuleTableResModel);
  }
}
